import React from "react";
import { DocumentNode, useApolloClient } from "@apollo/client";
import { IndexForms } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	exportFile,
	Heading,
	Modal,
	ModalProps,
	omitKeys,
	Row,
	toCSV,
	Tooltip,
	TooltipButton,
	TooltipButtonButtonProps,
	TooltipButtonTooltipProps,
	useUpdatingRef,
} from "@hex-insights/core";
import { Icon } from "../Icon";

// TODO move to core (and add types for queries)

export type ExportButtonProps<N, FI, O extends string | null, C extends string> = Omit<
	ExportButtonTooltipProps<N, FI, O, C>,
	keyof TooltipButtonTooltipProps
>;

export function ExportButton<N, FI, O extends string | null, C extends string>({
	queryVariables,
	dataQuery,
	paginationQuery,
	resultsPerPage,
	fileNamePrefix,
	resultKey,
	columns,
	formatRow,
}: ExportButtonProps<N, FI, O, C>) {
	return (
		<TooltipButton>
			{React.useCallback(
				(props: TooltipButtonTooltipProps) => (
					<ExportButton.Tooltip
						{...props}
						queryVariables={queryVariables}
						dataQuery={dataQuery}
						paginationQuery={paginationQuery}
						resultsPerPage={resultsPerPage}
						fileNamePrefix={fileNamePrefix}
						resultKey={resultKey}
						columns={columns}
						formatRow={formatRow}
					/>
				),
				[queryVariables, dataQuery, paginationQuery, resultsPerPage, fileNamePrefix, resultKey, columns, formatRow],
			)}
			{ExportButton.Button}
		</TooltipButton>
	);
}

type ExportButtonTooltipProps<N, FI, O extends string | null, C extends string> = TooltipButtonTooltipProps & {
	queryVariables: Pick<IndexForms.QueryVariables<FI, O>, "filters" | "order" | "pagination">;
	dataQuery: DocumentNode;
	paginationQuery: DocumentNode;
	resultsPerPage: number | null;
	fileNamePrefix?: string;
	resultKey: string;
	columns: readonly C[];
	formatRow: (node: N, data: any) => Record<C, any>;
};

ExportButton.Tooltip = function <N, FI, O extends string | null, C extends string>({
	queryVariables,
	dataQuery,
	paginationQuery,
	resultsPerPage,
	fileNamePrefix = "file",
	resultKey,
	columns,
	formatRow,
}: ExportButtonTooltipProps<N, FI, O, C>) {
	const client = useApolloClient();

	const onOnePageClick = React.useCallback(async () => {
		const { data } = await client.query({ query: dataQuery, variables: queryVariables });
		const file = toCSV(
			`${fileNamePrefix}-${new Date().getTime()}`,
			data[resultKey].edges.map((e: any) => formatRow(e.node, data)), // TODO types
			columns as C[], // TODO hex core update remove type assertion
		);
		exportFile(file);
	}, [client, dataQuery, queryVariables, fileNamePrefix, resultKey, columns, formatRow]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const isModalOpenRef = useUpdatingRef(isModalOpen);
	const [totalPages, setTotalPages] = React.useState(0);
	const [currentPage, setCurrentPage] = React.useState(0);

	const nonNullResultsPerPage = resultsPerPage ?? IndexForms.defaultResultsPerPage;
	const onAllPagesClick = React.useCallback(async () => {
		openModal();

		const { data: paginationData } = await client.query({
			query: paginationQuery,
			variables: omitKeys(queryVariables, ["pagination"]),
		});
		const connection = paginationData[resultKey];
		const totalPages = Math.ceil(connection.totalCount / nonNullResultsPerPage);
		setTotalPages(totalPages);
		const rows = [];
		for (let i = 0; i < totalPages; i++) {
			if (!isModalOpenRef.current) {
				setTotalPages(0);
				setCurrentPage(0);
				return;
			}
			const cursor = i > 0 ? connection.edges[i * nonNullResultsPerPage - 1].cursor : null;
			const { data } = await client.query({
				query: dataQuery,
				variables: {
					...queryVariables,
					pagination: { first: nonNullResultsPerPage, after: cursor },
				},
			});
			rows.push(...data[resultKey].edges.map((e: any) => formatRow(e.node, data)));
			setCurrentPage(i + 1);
		}

		const file = toCSV(`${fileNamePrefix}-${new Date().getTime()}`, rows, columns as C[]); // TODO hex core update remove type assertion
		exportFile(file);

		window.setTimeout(() => {
			closeModal();
		}, 750);
	}, [
		openModal,
		closeModal,
		client,
		dataQuery,
		paginationQuery,
		queryVariables,
		nonNullResultsPerPage,
		isModalOpenRef,
		fileNamePrefix,
		resultKey,
		columns,
		formatRow,
	]);

	const isDownloading = totalPages === 0 || currentPage < totalPages;

	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<Column>
					<Button
						variant="link"
						onClick={onOnePageClick}
						className="nowrap"
						style={{ textAlign: "center", width: "100%" }}
					>
						This Page Only
					</Button>
					<Button
						variant="link"
						onClick={onAllPagesClick}
						className="nowrap"
						style={{ textAlign: "center", width: "100%" }}
					>
						All Pages
					</Button>
				</Column>

				<Modal.If condition={isModalOpen}>
					<DownloadProgressModal
						value={currentPage}
						total={totalPages}
						isDownloading={isDownloading}
						onClose={closeModal}
					/>
				</Modal.If>
			</Tooltip.Body>
		</Tooltip>
	);
};

export type DownloadProgressModalProps = {
	value: number;
	total: number;
	isDownloading: boolean;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function DownloadProgressModal({
	value,
	total,
	isDownloading,
	ifRef,
	onClose,
	style,
	className,
}: DownloadProgressModalProps) {
	return (
		<Modal size="small" ifRef={ifRef} onClose={onClose} style={style} className={className}>
			<Modal.Header>
				<Heading.H2 noMargin>Downloading File</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" align="flex-end" verticalSpacing="0.125rem">
					<progress key={value} value={value} max={total} style={{ width: "100%", minWidth: "25rem" }} />
					<span style={{ fontSize: "0.75rem", color: isDownloading ? "#777" : "var(--success-color)" }}>
						{isDownloading ? "Downloading..." : "Complete"}
					</span>
				</Column>
			</Modal.Body>
			<Modal.Footer>
				<Row justify="flex-end">
					<Button variant="secondary" size="small" onClick={() => onClose?.()}>
						Cancel
					</Button>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

ExportButton.Button = function ({ onClick, disabled }: TooltipButtonButtonProps) {
	return (
		<Button variant="link" size="small" onClick={onClick} disabled={disabled}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
				<Icon.Export size={12} /> Export
			</Row>
		</Button>
	);
};
